import { useEpg } from '@nessprim/planby-pro'
import dayjs from 'dayjs'
import { useEffect, useState } from 'react'

import { useQueryGetAllBookings, useQueryGetAllItems } from '@flecto/core/saas'
import {
  type CoreBooking,
  type CoreItem,
  type CoreItemCategory,
  type ParameterID,
} from '@flecto/types'

import { useFlattenItemCategories } from '../../categories/hooks/useFlattenItemCategories'
import { type SchedulerChannel } from '../types/SchedulerChannel'
import { type SchedulerProgram } from '../types/SchedulerProgram'
import { useSchedulerFilters } from './useSchedulerFilters'

type Cat = {
  bookings: CoreBooking[]
  category: CoreItemCategory
  items: Map<ParameterID, CoreItem & { bookings: CoreBooking[] }>
}

export function useSchedulerDataGroupedByItems() {
  const [programs, setPrograms] = useState<SchedulerProgram[]>([])
  const [channels, setChannels] = useState<SchedulerChannel[]>([])
  const { endAt, showItems, startAt } = useSchedulerFilters()

  const {
    categories: dataItemsCategories,
    isLoading: isLoadingItemsCategories,
  } = useFlattenItemCategories()

  const { data: dataItems, isLoading: isLoadingItems } = useQueryGetAllItems()

  const { data: dataBookings, isLoading: isLoadingBookings } =
    useQueryGetAllBookings({
      from: startAt,
      to: endAt,
    })

  const isLoading =
    isLoadingBookings || isLoadingItems || isLoadingItemsCategories

  useEffect(() => {
    const categories = new Map<ParameterID, Cat>()
    const items = new Map<ParameterID, CoreItem & { bookings: CoreBooking[] }>()

    // Map to categories
    dataItemsCategories?.map((cat) => {
      categories.set(cat.id, { bookings: [], category: cat, items: new Map() })
    })

    dataItems?.item.map((item) => {
      items.set(item.internal_id, { ...item, bookings: [] })

      if (categories.has(item.category_id ?? '')) {
        const cat = categories.get(item.category_id ?? '')

        if (cat) {
          cat.items.set(item.internal_id, { ...item, bookings: [] })
          categories.set(item.category_id ?? '', cat)
        }
      }
    })

    // Map and associate bookings to items
    dataBookings?.booking?.map((booking) => {
      booking.booking_products?.map((bi) => {
        if (!items.has(bi.product?.internal_id)) {
          return
        }

        const item = items.get(bi.product?.internal_id)

        if (item) {
          const newItem = {
            ...item,
            bookings: [...item.bookings, booking],
          }

          items.set(bi.product?.internal_id, newItem)

          const categoryId = item.category_id ?? ''
          const cat = categories.get(categoryId)

          if (cat) {
            cat.items.set(bi.product?.internal_id, newItem)
            cat.bookings.push(booking)
            categories.set(categoryId, cat)
          }
        }
      })
    })

    const localPrograms: SchedulerProgram[] = []
    const localChannels: SchedulerChannel[] = []

    categories.forEach((cat, id) => {
      if (!cat.items.size) {
        return
      }

      if (showItems === 'active' && cat.bookings.length <= 0) {
        return
      }

      localChannels.push({
        groupTree: true,
        isOpen: cat.bookings.length > 0,
        itemCategory: cat.category,
        title: cat.category.name,
        type: 'channel',
        uuid: `cat-${id}`,
      })

      cat.items.forEach((item) => {
        if (showItems === 'active' && item.bookings.length <= 0) {
          return
        }

        localChannels.push({
          item,
          parentChannelUuid: `cat-${id}`,
          title: item.title,
          type: 'channel',
          uuid: `item-${item.id}`,
        })

        item.bookings.forEach((booking, index) => {
          const startAt = `${booking.start_at.split('T')[0]}T00:00:00`
          const endAt = `${booking.end_at.split('T')[0]}T23:59:59`

          localPrograms.push({
            booking,
            channelUuid: `item-${item.id}`,
            id: `booking-${item.id}-${booking.internal_id}`,
            index,
            item,
            since: startAt,
            till: endAt,
            title: `#${booking.internal_id} | ${booking.customer?.full_name}`,
          })
        })
      })
    })

    setPrograms(localPrograms)
    setChannels(localChannels)
  }, [dataBookings, dataItemsCategories, dataItems, showItems])

  const epg = useEpg({
    channels,
    dayWidth: 3500,
    endDate: `${dayjs(endAt).add(1, 'day').format('YYYY-MM-DD')}T00:00:00`,
    epg: programs,
    grid: {
      enabled: true,
    },
    isBaseTimeFormat: true,
    isLine: false,
    isResize: false,
    itemHeight: 56,
    mode: {
      style: 'modern',
      type: 'week',
    },
    overlap: {
      enabled: true,
      mode: 'stack',
    },
    sidebarWidth: 230,
    startDate: `${startAt}T00:00:00`,
    timelineDividers: 4,
  })

  return {
    epg,
    channels,
    isLoading,
    programs,
  }
}
